import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import {
  DatePickerCalendar,
  Icons,
  Typography,
} from '@usepepper/sprout-kit-ui';
import {
  formatTimePickerDate,
  formatTimePickerString,
  getNextValidTimeSlot,
  isToday,
  startOfDay,
  TIME_PICKER_MINUTE_STEPS,
} from '@/experiences/sales-portal/blocs/utils/date-picker-calendar-utils';
import { get12HourFormat, militaryTimeToHourAndMinutes } from '@/utils/date';
import { Pressable, ScrollView, View } from 'react-native';
import { TimePickerModal } from '@/experiences/sales-portal/components/time-picker-modal';

const ICON_SIZE = 16;
const TIME_TRIGGER_HEIGHT = 40;

interface CheckInReminderContentProps {
  onDateChange: (dates: Date[]) => void;
  onInvalidTime: () => void;
  onTimeChange: (time: string) => void;
  selectedDate?: Date;
  selectedTime: string;
}

export const CheckInReminderContent: React.FC<CheckInReminderContentProps> = ({
  onDateChange,
  onInvalidTime,
  onTimeChange,
  selectedDate,
  selectedTime,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

  const timeLabel = i18n.t('sales_portal.activity_note.reminder.time_label');
  const formattedTime = get12HourFormat(
    ...militaryTimeToHourAndMinutes(selectedTime),
  );

  const minimumTime = isToday(selectedDate ?? new Date())
    ? getNextValidTimeSlot(TIME_PICKER_MINUTE_STEPS)
    : undefined;

  const handleTimeConfirm = (time: Date) => {
    setIsTimePickerOpen(false);
    if (minimumTime && time < minimumTime) {
      onInvalidTime();
      return;
    }
    onTimeChange(formatTimePickerString(time));
  };

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <DatePickerCalendar
          minDate={startOfDay(new Date())}
          mode="single"
          onChange={onDateChange}
          value={selectedDate ? [selectedDate] : []}
          width="100%"
        />
        <View style={styles.timeField}>
          <Typography color="$textSecondary" variant="label">
            {timeLabel}
          </Typography>
          <Pressable
            accessibilityLabel={`${timeLabel}: ${formattedTime}`}
            accessibilityRole="button"
            aria-expanded={isTimePickerOpen}
            onPress={() => setIsTimePickerOpen(true)}
            style={({ hovered, pressed }) => [
              styles.timeTrigger,
              hovered || pressed ? styles.timeTriggerActive : null,
            ]}
          >
            <Typography color="$textPrimary" flex={1} size="$md">
              {formattedTime}
            </Typography>
            <Icons.ChevronDown
              color={theme.sk.color.icon.tertiary}
              size={ICON_SIZE}
            />
          </Pressable>
        </View>
      </ScrollView>
      {isTimePickerOpen ? (
        <TimePickerModal
          accessibilityLabel={timeLabel}
          minimumDate={minimumTime}
          minuteInterval={TIME_PICKER_MINUTE_STEPS}
          onCancel={() => setIsTimePickerOpen(false)}
          onConfirm={handleTimeConfirm}
          time={formatTimePickerDate(selectedDate ?? new Date(), selectedTime)}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  scrollContent: {
    gap: theme.sk.spacing.spaceMedium,
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  timeField: {
    gap: theme.sk.spacing.spaceSmall,
  },
  timeTrigger: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    height: TIME_TRIGGER_HEIGHT,
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
  },
  timeTriggerActive: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
}));
